import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Form, Modal, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import {
  useListEntrepreneurApplicationsQuery,
  useListEntrepreneurUsersQuery,
  useCreateEntrepreneurFirmMutation,
  useSetFirmStageMutation,
  useBulkUploadEntrepreneurUsersMutation,
  useListPendingPortalInvitesQuery,
  useSendPortalInvitesMutation,
} from '../../app/api';
import { brand, cardStyle, headStyle, EntPage, FilterBar, filterInputStyle, StageBadge } from './common';

/* Entrepreneur -> Firms: the hub directory. Every firm and the people who log
 * in to its workspace (announcements, resources, training). Each firm drills
 * into its hub page (FirmDetail).
 *
 * Nothing about Claude is shown here on purpose - that's an optional add-on
 * with its own list under Claude -> Claude Firms. Mixing the two on one page
 * is what made this screen confusing.
 */

// Hub-only "needs attention" hint from the list payload alone.
function attentionFor(a) {
  if ((a.member_count ?? a.active_seats ?? 0) === 0) return 'No members yet';
  if ((a.admin_count ?? 0) === 0) return 'No firm admin';
  return null;
}

export default function Firms() {
  const navigate = useNavigate();
  const { data: firms = [], isLoading, refetch } = useListEntrepreneurApplicationsQuery();
  const [createFirm] = useCreateEntrepreneurFirmMutation();
  const [setFirmStage] = useSetFirmStageMutation();
  const [bulkUpload] = useBulkUploadEntrepreneurUsersMutation();

  const [search, setSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return firms.filter(a => !q || (a.firm_name || '').toLowerCase().includes(q));
  }, [firms, search]);

  // User lookup across all firms — jumps to the firm a person belongs to. Only
  // loads the user list once the employee actually types a query.
  const userQ = userSearch.trim().toLowerCase();
  const { data: allUsers = [], isFetching: usersLoading } = useListEntrepreneurUsersQuery(undefined, { skip: !userQ });
  const matchedUsers = useMemo(() => {
    if (!userQ) return [];
    return allUsers.filter(u => `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''} ${u.firm_name || ''}`.toLowerCase().includes(userQ));
  }, [allUsers, userQ]);

  // ── New firm modal ──
  const [showFirm, setShowFirm] = useState(false);
  const [firmName, setFirmName] = useState('');
  const [firmTransitioning, setFirmTransitioning] = useState(false);
  const [firmSaving, setFirmSaving] = useState(false);
  const [firmError, setFirmError] = useState(null);

  const submitFirm = async (e) => {
    e.preventDefault();
    if (!firmName.trim()) { setFirmError('Firm name is required.'); return; }
    setFirmSaving(true); setFirmError(null);
    try {
      const res = await createFirm({ name: firmName.trim() }).unwrap();
      // Jump straight into the new firm so the employee can add users next.
      const newId = res?.id || res?.firm_id;
      // Before anyone is added, so nobody at a transitioning firm ever signs
      // in to the full workspace.
      if (newId && firmTransitioning) await setFirmStage({ firm_id: newId, stage: 'transitioning' }).unwrap();
      setFirmName(''); setFirmTransitioning(false); setShowFirm(false);
      await refetch();
      if (newId) navigate(`/entrepreneur/firms/${newId}`);
    } catch (err) {
      setFirmError(err?.data?.detail || 'Could not create firm.');
    }
    setFirmSaving(false);
  };

  // ── Bulk upload modal (cross-firm — the sheet names each user's firm) ──
  const [showBulk, setShowBulk] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  // Off by default: the usual path is import first, then "Send portal invites".
  const [bulkSendEmail, setBulkSendEmail] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkInputRef = useRef(null);

  const openBulk = () => {
    setBulkFile(null); setBulkSendEmail(false); setBulkError(null); setBulkResult(null);
    if (bulkInputRef.current) bulkInputRef.current.value = '';
    setShowBulk(true);
  };

  // dryRun = Preview: the server reports what it would do and writes nothing.
  const submitBulk = async (dryRun) => {
    if (!bulkFile) { setBulkError('Choose an .xlsx or .csv file.'); return; }
    setBulkBusy(true); setBulkError(null); setBulkResult(null);
    const fd = new FormData();
    fd.append('file', bulkFile);
    fd.append('send_email', bulkSendEmail && !dryRun ? 'true' : 'false');
    fd.append('dry_run', dryRun ? 'true' : 'false');
    try {
      const res = await bulkUpload(fd).unwrap();
      setBulkResult(res);
      if (!dryRun) await refetch();
    } catch (err) {
      setBulkError(err?.data?.detail || 'Bulk upload failed.');
    }
    setBulkBusy(false);
  };

  const [showInvites, setShowInvites] = useState(false);

  const actions = (
    <>
      <Button size="sm" style={{ background: brand.goldenrod, border: 'none', color: brand.deepBlue, fontWeight: 700 }} onClick={() => { setFirmName(''); setFirmError(null); setShowFirm(true); }}>
        <i className="bi bi-building-add me-1" />New firm
      </Button>
      <Button variant="outline-light" size="sm" onClick={openBulk}>
        <i className="bi bi-file-earmark-arrow-up me-1" />Bulk upload members
      </Button>
      <Button variant="outline-light" size="sm" onClick={() => setShowInvites(true)}>
        <i className="bi bi-envelope-paper me-1" />Send portal invites
      </Button>
    </>
  );

  return (
    <EntPage title="Firms" subtitle="Every Entrepreneur firm and the people who log in to its hub. Claude is managed separately under Claude → Claude Firms." actions={actions}>
      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>All firms</span>
          <FilterBar search={search} onSearchChange={setSearch} placeholder="Search firms…">
            <input
              type="search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search members…"
              aria-label="Search members"
              style={{ ...filterInputStyle, width: 200 }}
            />
          </FilterBar>
        </Card.Header>
        <Card.Body className="p-0">
          {userQ ? (
            <UserResults
              users={matchedUsers}
              loading={usersLoading}
              onPick={(u) => navigate(`/entrepreneur/firms/${u.firm_id}`)}
            />
          ) : isLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : rows.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
              <i className="bi bi-building" style={{ fontSize: '1.75rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
              {firms.length === 0 ? 'No firms yet. Create one to get started.' : 'No firms match these filters.'}
            </div>
          ) : (
            <Table hover responsive className="mb-0" style={{ fontSize: '0.88rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={{ padding: '0.6rem 0.9rem' }}>Firm</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Members</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Firm admins</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Awaiting first login</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Needs attention</th>
                <th style={{ padding: '0.6rem 0.9rem', width: 30 }} />
              </tr></thead>
              <tbody>
                {rows.map(a => {
                  const attention = attentionFor(a);
                  return (
                    <tr key={a.firm_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/entrepreneur/firms/${a.firm_id}`)}>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 700, color: brand.deepBlue }}>
                          {a.firm_name}<StageBadge stage={a.stage} style={{ marginLeft: 8 }} />
                        </div>
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>
                        {a.member_count ?? a.active_seats}
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{a.admin_count ?? 0}</td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{a.awaiting_first_login ?? 0}</td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle' }}>
                        {attention ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: brand.goldenrod, fontWeight: 600, fontSize: '0.78rem' }}>
                            <i className="bi bi-exclamation-circle-fill" />{attention}
                          </span>
                        ) : (
                          <span style={{ color: brand.green, fontSize: '0.78rem' }}><i className="bi bi-check-circle me-1" />All set</span>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', textAlign: 'right' }}>
                        <i className="bi bi-chevron-right" style={{ color: brand.tealBlue }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* New firm modal */}
      <Modal show={showFirm} onHide={() => setShowFirm(false)} centered>
        <Form onSubmit={submitFirm}>
          <Modal.Header closeButton><Modal.Title style={{ fontSize: '1.1rem', color: brand.deepBlue }}>New B2B firm</Modal.Title></Modal.Header>
          <Modal.Body>
            {firmError && <Alert variant="danger" className="py-2">{firmError}</Alert>}
            <Form.Group>
              <Form.Label>Firm name</Form.Label>
              <Form.Control autoFocus value={firmName} onChange={e => setFirmName(e.target.value)} placeholder="e.g. Acme Advisory" />
            </Form.Group>
            <Form.Check className="mt-3" id="new-firm-transitioning" checked={firmTransitioning}
              onChange={e => setFirmTransitioning(e.target.checked)}
              label="Still transitioning - members see only their transition dashboard until you take the firm live" />
            <p style={{ fontSize: '0.8rem', color: brand.charcoal, marginTop: '0.75rem', marginBottom: 0 }}>
              <i className="bi bi-info-circle me-1" />Next you&apos;ll add the firm&apos;s members - each gets a hub login invite. Claude is optional and can be set up later.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowFirm(false)}>Cancel</Button>
            <Button type="submit" disabled={firmSaving} style={{ background: brand.deepBlue, border: 'none' }}>
              {firmSaving ? 'Creating…' : 'Create firm'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Bulk upload modal */}
      <Modal show={showBulk} onHide={() => setShowBulk(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{ fontSize: '1.1rem', color: brand.deepBlue }}>Bulk upload members</Modal.Title></Modal.Header>
        <Modal.Body>
          {bulkError && <Alert variant="danger" className="py-2">{bulkError}</Alert>}
          <p style={{ fontSize: '0.85rem', color: brand.charcoal }}>
            Upload an <strong>.xlsx</strong> or <strong>.csv</strong> with columns: <em>email, first name, last name, firm</em>.
            Optional: <em>fsc_office_id</em> (matches the firm by its FSC office first), <em>role</em> (admin or standard),
            <em> training_date</em> + <em>training_time</em> (pre-registers for that training session), and
            <em> claude seat</em> (&quot;yes&quot; also requests a Claude seat).
          </p>
          <p style={{ fontSize: '0.8rem', color: brand.charcoal }}>
            <i className="bi bi-shield-check me-1" />Existing firms and existing members are never changed - they are
            reported as already set up. Use <strong>Preview</strong> first; it saves nothing.
          </p>
          <Form.Control ref={bulkInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={e => { setBulkFile(e.target.files[0] || null); setBulkResult(null); }} />
          <Form.Check
            className="mt-2"
            type="checkbox"
            id="bulk-send-email"
            label="Email login instructions now (or leave off and use Send portal invites later)"
            checked={bulkSendEmail}
            onChange={e => setBulkSendEmail(e.target.checked)}
          />
          {bulkResult && <BulkResult result={bulkResult} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowBulk(false)}>Close</Button>
          <Button variant="outline-secondary" onClick={() => submitBulk(true)} disabled={bulkBusy || !bulkFile}>
            Preview
          </Button>
          <Button onClick={() => submitBulk(false)} disabled={bulkBusy || !bulkFile} style={{ background: brand.deepBlue, border: 'none' }}>
            {bulkBusy ? 'Working…' : 'Import'}
          </Button>
        </Modal.Footer>
      </Modal>

      {showInvites && <PortalInvitesModal onClose={() => { setShowInvites(false); refetch(); }} />}
    </EntPage>
  );
}

// Result of a bulk upload or its Preview (dry run).
function BulkResult({ result }) {
  const s = result.summary || {};
  const list = (items, render, color) => items?.length > 0 && (
    <div style={{ color, marginTop: '0.35rem' }}>
      {items.slice(0, 10).map((x, i) => <div key={i}>{render(x)}</div>)}
      {items.length > 10 && <div>…and {items.length - 10} more.</div>}
    </div>
  );
  return (
    <div className="mt-3" style={{ fontSize: '0.85rem' }}>
      {result.dry_run && <Alert variant="info" className="py-2 mb-2">Preview only - nothing was saved. These are the changes Import would make.</Alert>}
      <div className="d-flex gap-2 flex-wrap mb-2">
        <Badge bg="success">{result.dry_run ? 'Would create' : 'Created'} {s.created}</Badge>
        <Badge bg="secondary">Already set up {s.skipped}</Badge>
        <Badge bg="warning" text="dark">Employee conflicts {s.conflicts}</Badge>
        <Badge bg="danger">Errors {s.errors}</Badge>
        <Badge bg="info">New firms {s.firms_created}</Badge>
        <Badge bg="info">Linked to FSC office {s.firms_linked ?? 0}</Badge>
        <Badge bg="primary">Training {s.training_registered ?? 0}</Badge>
        {(s.training_errors ?? 0) > 0 && <Badge bg="danger">Training errors {s.training_errors}</Badge>}
      </div>
      {list(result.errors, e => `Row ${e.row}: ${e.error}${e.email ? ` (${e.email})` : ''}`, brand.red)}
      {list(result.training_errors, e => `Row ${e.row}: ${e.error} (${e.email})`, brand.red)}
      {list(result.conflicts, c => `Row ${c.row}: ${c.email} is an employee account - skipped`, brand.charcoal)}
      {list(result.notes, n => `${n.firm}: ${n.note}`, brand.charcoal)}
      {list((result.skipped || []).filter(x => x.reason === 'already_exists_elsewhere'),
        x => `Row ${x.row}: ${x.email} already belongs to another firm - left unchanged`, brand.charcoal)}
    </div>
  );
}
BulkResult.propTypes = { result: PropTypes.object.isRequired };

// "Send portal invites": members who have never signed in and have no recorded
// invite, by firm. Sends firm by firm so each request stays short and progress
// is visible. Each invite issues a fresh temporary password.
function PortalInvitesModal({ onClose }) {
  const { data, isLoading, refetch } = useListPendingPortalInvitesQuery();
  const [sendInvites] = useSendPortalInvitesMutation();
  const firms = data?.firms || [];
  const [picked, setPicked] = useState(null);           // null = all, until the user changes it
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);       // {done, total, sent, failed: []}
  const [confirming, setConfirming] = useState(false);

  const selected = picked ?? new Set(firms.map(f => f.firm_id));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };
  const people = firms.filter(f => selected.has(f.firm_id)).reduce((n, f) => n + f.count, 0);

  const send = async () => {
    setConfirming(false); setBusy(true);
    const ids = firms.filter(f => selected.has(f.firm_id)).map(f => f.firm_id);
    const prog = { done: 0, total: ids.length, sent: 0, failed: [] };
    setProgress({ ...prog });
    for (const id of ids) {
      try {
        const res = await sendInvites([id]).unwrap();
        prog.sent += res.sent || 0;
        prog.failed.push(...(res.failed || []));
      } catch (err) {
        const name = firms.find(f => f.firm_id === id)?.firm_name;
        prog.failed.push({ email: name, error: err?.data?.detail || 'request failed' });
      }
      prog.done += 1;
      setProgress({ ...prog });
    }
    setBusy(false); setPicked(null);
    refetch();
  };

  return (
    <Modal show onHide={busy ? undefined : onClose} centered size="lg">
      <Modal.Header closeButton={!busy}><Modal.Title style={{ fontSize: '1.1rem', color: brand.deepBlue }}>Send portal invites</Modal.Title></Modal.Header>
      <Modal.Body style={{ fontSize: '0.88rem' }}>
        <p style={{ color: brand.charcoal }}>
          Everyone below has a hub login but has never signed in and has not been sent an invite. Each person gets the
          welcome email with a <strong>new temporary password</strong>. Members already using the hub are never included.
        </p>
        {progress && (
          <Alert variant={progress.failed.length ? 'warning' : 'success'} className="py-2">
            {busy ? `Sending… ${progress.done}/${progress.total} firms` : `Done: ${progress.sent} invite(s) sent.`}
            {progress.failed.length > 0 && (
              <div className="mt-1">{progress.failed.slice(0, 10).map((f, i) => <div key={i}>{f.email}: {f.error}</div>)}</div>
            )}
          </Alert>
        )}
        {isLoading ? <div className="text-center p-3"><Spinner size="sm" /></div> : firms.length === 0 ? (
          <div style={{ color: brand.charcoal }}>Nobody is waiting for an invite.</div>
        ) : (
          <Table size="sm" className="mb-0">
            <thead><tr>
              <th style={{ width: 30 }}>
                <Form.Check type="checkbox" aria-label="Select all" checked={selected.size === firms.length}
                  onChange={e => setPicked(e.target.checked ? new Set(firms.map(f => f.firm_id)) : new Set())} disabled={busy} />
              </th>
              <th>Firm</th><th>People</th><th>Who</th>
            </tr></thead>
            <tbody>
              {firms.map(f => (
                <tr key={f.firm_id}>
                  <td><Form.Check type="checkbox" aria-label={f.firm_name} checked={selected.has(f.firm_id)} onChange={() => toggle(f.firm_id)} disabled={busy} /></td>
                  <td style={{ fontWeight: 600, color: brand.deepBlue }}>{f.firm_name}</td>
                  <td>{f.count}</td>
                  <td style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{f.users.map(u => u.email).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        {confirming ? (
          <>
            <span style={{ marginRight: 'auto', color: brand.charcoal }}>Email {people} people in {selected.size} firm(s)?</span>
            <Button variant="light" onClick={() => setConfirming(false)}>Back</Button>
            <Button onClick={send} style={{ background: brand.deepBlue, border: 'none' }}>Yes, send</Button>
          </>
        ) : (
          <>
            <Button variant="light" onClick={onClose} disabled={busy}>Close</Button>
            <Button onClick={() => setConfirming(true)} disabled={busy || people === 0} style={{ background: brand.deepBlue, border: 'none' }}>
              {busy ? 'Sending…' : `Send ${people} invite${people === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
PortalInvitesModal.propTypes = { onClose: PropTypes.func.isRequired };

// Cross-firm user lookup results — each row jumps to the firm the user is under.
function UserResults({ users, loading, onPick }) {
  if (loading) return <div className="text-center p-4"><Spinner size="sm" /></div>;
  if (users.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
        <i className="bi bi-person-x" style={{ fontSize: '1.75rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
        No users match your search.
      </div>
    );
  }
  return (
    <Table hover responsive className="mb-0" style={{ fontSize: '0.88rem' }}>
      <thead><tr style={{ color: brand.charcoal }}>
        <th style={{ padding: '0.6rem 0.9rem' }}>Name</th>
        <th style={{ padding: '0.6rem 0.9rem' }}>Email</th>
        <th style={{ padding: '0.6rem 0.9rem' }}>Firm</th>
        <th style={{ padding: '0.6rem 0.9rem' }}>Status</th>
        <th style={{ padding: '0.6rem 0.9rem', width: 30 }} />
      </tr></thead>
      <tbody>
        {users.map(u => (
          <tr key={u.uuid} style={{ cursor: 'pointer' }} onClick={() => onPick(u)}>
            <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.deepBlue, fontWeight: 600 }}>
              {[u.first_name, u.last_name].filter(Boolean).join(' ') || <span style={{ color: brand.charcoal }}>—</span>}
            </td>
            <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{u.email}</td>
            <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.deepBlue }}>{u.firm_name || '—'}</td>
            <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle' }}>
              {u.needs_password_change
                ? <Badge bg="warning" text="dark">Pending first login</Badge>
                : <Badge bg="success">Active</Badge>}
            </td>
            <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', textAlign: 'right' }}>
              <i className="bi bi-chevron-right" style={{ color: brand.tealBlue }} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
UserResults.propTypes = {
  users: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onPick: PropTypes.func.isRequired,
};
